import { useEffect, useState } from "react";
import { useProblem } from "../../contexts/problem-context";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  Tooltip as NextTooltip,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
} from "@nextui-org/react";
import { Bar } from "react-chartjs-2";
import fetchWrapper from "../../utils/fetch";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@nextui-org/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/auth-context";
import { toast } from "react-toastify";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export const options = {
  responsive: true,
  animation: {
    duration: 1000, // Set the duration of the animation in milliseconds
    easing: "easeOutExpo", // Apply a specific easing function for a more natural animation
  },
  plugins: {
    legend: {
      position: "top",
      labels: {
        // This more specific font property overrides the global property
        font: {
          size: 14,
          family: "iranyekan",
        },
      },
    },
    title: {
      display: true,
      text: "نتایج",
      font: {
        size: 14,
        family: "iranyekan",
      },
    },
    tooltip: {
      backgroundColor: "rgba(0, 0, 0, 0.6)", // Customize the background color and opacity of the tooltip
      padding: 10, // Add padding to the tooltip content
      displayColors: false, // Hide the color indicators in the tooltip
      bodyFont: {
        family: "iranyekan", // Set the font family for the tooltip content
        size: 14, // Adjust the font size of the tooltip content
      },
      titleFont: {
        family: "iranyekan", // Set the font family for the tooltip content
        size: 14, // Adjust the font size of the tooltip content
      },
    },
  },
  scales: {
    yAxes: [
      {
        ticks: {
          fontFamily: "iranyekan", // Setting the font to inherit from the website's default font
        },
      },
    ],
    xAxes: [
      {
        ticks: {
          fontFamily: "iranyekan", // Setting the font to inherit from the website's default font
        },
      },
    ],
    x: {
      grid: {
        display: true,
        color: "rgba(0, 0, 0, 0.1)", // Adjust the color and opacity of the grid lines
        lineWidth: 1, // Customize the thickness of the grid lines
        borderDash: [5, 5], // Apply a dashed line style for the grid
      },
      ticks: {
        font: {
          family: "iranyekan", // Set the font family for the x-axis labels at the bottom of each bar
          size: 12, // Adjust the font size of the x-axis labels
          weight: "bold", // Set the font weight for emphasis, if needed
        },
      },
    },
  },
  elements: {
    bar: {
      backgroundColor: "#B2CCFF", // Set the recommended color for the bars
      borderColor: "#B2CCFF", // Set the border color to match the bar color
      borderWidth: 1, // Adjust the border width as needed
      borderSkipped: "bottom",
      shadowOffsetX: 2, // Add a subtle horizontal shadow offset
      shadowOffsetY: 2, // Add a subtle vertical shadow offset
      shadowBlur: 5, // Apply a blur effect to the shadow
      shadowColor: "rgba(0, 0, 0, 0.3)", // Set the color and opacity of the shadow
    },
  },
  layout: {
    padding: {
      top: 20, // Add extra padding at the top of the chart
      bottom: 30, // Add extra padding at the bottom of the chart
      left: 10, // Add extra padding to the left of the chart
      right: 10, // Add extra padding to the right of the chart
    },
  },
};

const labels = ["a1", "a2", "b1", "b2"];

export const DEFAULT_DATA = {
  labels,
  datasets: [
    {
      label: "وزن نسبی",
      data: labels.map(() => Math.random() * 50),
      // backgroundColor: "rgba(255, 99, 132, 0.5)",
    },
  ],
};

function normalizeNumbers(numbers) {
  // Calculate the sum of all numbers
  const sum = numbers.reduce((total, num) => total + (num ?? 0), 0);

  // Normalize each number based on its percentage
  const normalizedNumbers = numbers.map((num) =>
    (((num ?? 0) / sum) * 100).toFixed(2),
  );

  return normalizedNumbers;
}

function normalizeObjValues(obj) {
  let values = Object.values(obj);
  let sumVal = values.reduce((a, b) => a + b, 0);

  // Get normalized object
  let normalizedObj = {};
  for (let key in obj) {
    normalizedObj[key] = parseFloat(((obj[key] / sumVal) * 100).toFixed(2));
  }
  return normalizedObj;
}

export default function ProblemResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [data, setData] = useState(DEFAULT_DATA);
  const [data2, setData2] = useState(DEFAULT_DATA);
  const [activeTitle, setActiveTitle] = useState("");
  const [points, setPoints] = useState({});
  const [
    {
      answers,
      alternatives,
      varriables,
      problemType,
      weights,
      accuracy,
      user_preferences,
    },
    problemDispatch,
  ] = useProblem();

  const [{ isAuthenticated }] = useAuth();

  useEffect(() => {
    const problem_id = searchParams.get("problem_id");
    if (!problem_id) {
      toast.error("تصمیمی انتخاب نشده است.");
      navigate("/");
      return;
    }
    setIsLoading(true);
    fetchWrapper
      .post("/get/problem", {
        problem_uid: problem_id,
      })
      .then((data) => {
        const vs = {};
        const ws = [];
        const ps = [];
        data.criteria_names.map((varriableKey, varriableI) => {
          vs[varriableKey] = data.rate_names[varriableI];
          if (data.problem_type === "saw") {
            ws.push(Math.floor(data.weights[varriableI][0]));
            ps.push(data.weights[varriableI][1] ? true : false);
          }
          return varriableKey;
        });
        problemDispatch({
          type: "load problem",
          payload: {
            alternatives: data.alternatives,
            criteria_rates: data.criteria_rates,
            problemType: data.problem_type,
            weights: data.weights,
            varriables: vs,
            accuracy: data.accuracy,
            user_preferences: data.user_preferences,
            template_id: problem_id,
          },
        });
      })
      .catch((err) => {
        if (err?.detail === "not found") {
          toast.error("این تصمیم موجود نیست");
          navigate("/");
        } else if (err?.detail === "Invalid credentials") {
          toast.error("برای استفاده باید وارد شوید.");
          navigate("/login");
        } else {
          toast.error(err);
        }
      });
    fetchWrapper
      .post("/get/answer", {
        problem_uid: problem_id,
      })
      .then((data) => {
        problemDispatch({
          type: "update answers",
          payload: {
            answers: data.answer,
            user_preferences: data.user_preferences,
            accuracy: problemType === "saw" ? 0 : data.accuracy,
          },
        });
        setIsLoading(false);
      })
      .catch((err) => {
        toast.error(err);
        setIsLoading(false);
      });
  }, [problemDispatch, setIsLoading, problemType, navigate, searchParams]);

  useEffect(() => {
    if (answers && user_preferences) {
      setIsLoading(true);
      let lastLabels = {};
      let lastData = [];
      let label = "وزن نسبی";
      if (problemType === "saw") {
        label = "رتبه‌بندی گزینه‌ها";
        weights.map((w) => {
          return lastData.push(w[0]);
        });
        const ps = {};
        Object.keys(answers).map((alternative, index) => {
          // let sum = Object.values(alternative).reduce();
          let point = Object.values(answers[alternative]).reduce(
            (accumulator, currentValue) => {
              return accumulator + currentValue;
            },
            0,
          );
          ps[alternative] = point;
          return alternative;
        });
        const sorted = Object.entries(ps)
          .sort(([, a], [, b]) => b - a)
          .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});
        const ss = normalizeObjValues(sorted);
        setPoints(ss);
        lastLabels = Object.keys(ss);
        lastData = Object.values(ss);
      } else {
        const lastObj = {};
        const newLabels = Object.keys(answers);
        Object.keys(varriables).map((varriableKey) => {
          newLabels.map((label) => {
            if (label.startsWith(varriableKey)) {
              if (lastObj[varriableKey]) {
                lastObj[varriableKey] = lastObj[varriableKey] + answers[label];
              } else {
                lastObj[varriableKey] = answers[label];
              }
            }
            return label;
          });
          /// This formula should not be used for special equal cases.
          if (
            user_preferences.includes(">=") ||
            user_preferences.includes("<=")
          ) {
            console.log("used the formula");
            lastObj[varriableKey] =
              lastObj[varriableKey] -
              (varriables[varriableKey].length *
                (varriables[varriableKey].length - 1)) /
                2;
            lastObj[varriableKey] = lastObj[varriableKey] + 1;
          }
          return varriableKey;
        });
        lastLabels = Object.keys(lastObj);
        lastData = normalizeNumbers(Object.values(lastObj));

        const ps = {};
        Object.keys(alternatives).map((alternativeKey, index) => {
          // let sum = Object.values(alternative).reduce();
          let point = Object.keys(alternatives[alternativeKey]).reduce(
            (accumulator, currentValue) => {
              return (
                accumulator +
                (answers[
                  `${currentValue}_${alternatives[alternativeKey][currentValue]}`
                ] ?? 0)
              );
            },
            0,
          );
          if (!isNaN(parseFloat(alternativeKey)) && isFinite(alternativeKey)) {
            ps[`گزینه‌ی ${alternativeKey.toString()}`] = point;
          } else {
            ps[alternativeKey] = point;
          }
          return alternativeKey;
        });
        const sorted = Object.entries(ps)
          .sort(([, a], [, b]) => b - a)
          .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});
        setPoints(normalizeObjValues(sorted));
      }
      setData({
        labels: lastLabels,
        datasets: [
          {
            label: label,
            data: lastData,
            // backgroundColor: "rgba(255, 99, 132, 0.5)",
          },
        ],
      });
      setIsLoading(false);
    }
  }, [
    answers,
    user_preferences,
    setIsLoading,
    setData,
    alternatives,
    problemType,
    varriables,
    weights,
  ]);

  function openModal(alternativeKey) {
    let lastObj = {};
    if (problemType === "saw") {
      lastObj = answers[alternativeKey];
    } else {
      Object.keys(alternatives[alternativeKey]).map((varriableKey) => {
        lastObj[varriableKey] =
          answers[
            `${varriableKey}_${alternatives[alternativeKey][varriableKey]}`
          ];
        return varriableKey;
      });
    }
    const lastLabels = Object.keys(lastObj);
    const lastData = normalizeNumbers(Object.values(lastObj));
    setData2({
      labels: lastLabels,
      datasets: [
        {
          label: "وزن نسبی",
          data: lastData,
          // backgroundColor: "rgba(255, 99, 132, 0.5)",
        },
      ],
    });
    setActiveTitle(alternativeKey);
    onOpen();
  }

  // const alternativePoints = Object.keys(alternatives).forEach(
  //   function (key, index) {
  //     alternatives[key]
  //   },
  // );

  return isLoading ? (
    <div>
      <Spinner size="lg" />;
    </div>
  ) : (
    <div>
      <div className="text-center space-y-4 mb-16">
        <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          {problemType === "saw" ? "رتبه‌بندی گزینه‌ها" : "نتایج"}
        </h3>
        <h4 className="text-xl md:text-2xl text-gray-600">
          در این بخش وزن نسبی معیارها (ترجیحات شما) بر اساس پاسخهای شما نمایش
          داده شده است
        </h4>
      </div>
      {<Bar options={options} data={data} />}
      {problemType !== "saw" ? (
        <div className="flex flex-row items-center pt-6">
          <h3 className="font-bold text-md mx-2">دقت: {accuracy * 100}%</h3>
          {/* <NextTooltip
            content="با پاسخ دادن به سوالات بیشتر می‌توانید دقت محاسبات را بیشتر کنید (این امکان در حال حاضر وجود ندارد.)."
            showArrow={true}
            color="primary"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
              />
            </svg>
          </NextTooltip> */}
          <Popover placement="left" color="primary">
            <PopoverTrigger>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                />
              </svg>
            </PopoverTrigger>
            <PopoverContent>
              <div className="px-1 py-2">
                <div className="text-small font-bold">
                  با پاسخ دادن به سوالات بیشتر می‌توانید دقت محاسبات را بیشتر
                  کنید (این امکان در حال حاضر وجود ندارد.).
                </div>
                {/* <div className="text-tiny">This is the popover content</div> */}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      ) : null}
      <h3 className="mt-6 font-bold text-md">نتایج گزینه‌ها</h3>
      {Object.keys(points).map((alternative, i) => (
        <li
          key={i}
          className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 py-3 sm:flex-nowrap"
        >
          {/* border-gray-400 border-orange-600 border-green-600 */}
          <div
            className={`flex flex-row bg-gray-200 items-center border-t-[4px] border-${
              i === 0
                ? "green-600"
                : i === 1 || i === 2
                ? "orange-600"
                : "gray-400"
            }
            min-w-[25rem]`}
          >
            {/* <p className="text-sm font-semibold leading-6 text-gray-900">
              <button
                onClick={() => openModal(alternative)}
                className="underline text-md text-blue-700 hover:text-blue-800"
              >
                {i + 1}: {alternative} , امتیاز: {points[alternative]}
              </button>
            </p>
            <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
              <p>
                <span className="hover:underline">
                  رده‌بندی: <span className="font-mono">{i + 1}</span>
                </span>
              </p>
            </div> */}
            {/* size-16 size-12 size-10 */}
            <div
              className={`bg-gray-400 px-3 py-1 text-center flex flex-col items-center justify-center size-${
                i === 0 ? "16" : i === 1 || i === 2 ? "12" : "10"
              }`}
            >
              <div>
                <span
                  className={`text-${
                    i === 0 ? "3xl" : i === 1 || i === 2 ? "xl" : "base"
                  } text-white`}
                >
                  {i + 1}
                </span>
                {/* <span className="text-lg text-white"></span> */}
              </div>
            </div>
            <div className="flex flex-col mr-2 px-2 h-full">
              <div className="">
                {/* text-xl texd-lg text-base text-3xl text-2xl */}
                <span
                  className={`"text-gray-900 text-${
                    i === 0 ? "2xl" : i === 1 || i === 2 ? "xl" : "base"
                  }`}
                >
                  {alternative}
                </span>{" "}
                <p>
                  <span className="text-xs mr-3">
                    امتیاز: {points[alternative]}
                  </span>
                  <span
                    className="text-xs mr-3 text-blue-600 underline underline-offset-4 cursor-pointer"
                    onClick={() => openModal(alternative)}
                  >
                    مشاهده
                  </span>{"  "}

                  {/* Add Technolife link if present */}
                  {alternatives[alternative] &&
                    alternatives[alternative].technolife_link && (
                      <a
                        href={alternatives[alternative].technolife_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs ml-3 text-green-700 underline underline-offset-4 cursor-pointer"
                      >
                        لینک خرید
                      </a>
                    )}
                </p>
              </div>
              <div></div>
            </div>
          </div>
        </li>
      ))}
      {isAuthenticated && (
        <div>
          <Link to="/problem/type" reloadDocument>
            <Button
              color="success"
              className="w-full font-bold text-lg px-10 py-6 mt-10"
            >
              ساخت تصمیم جدید
            </Button>
          </Link>
        </div>
      )}

      <Modal
        isOpen={isOpen}
        size="4xl"
        placement={"center"}
        onOpenChange={onOpenChange}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                {activeTitle}
              </ModalHeader>
              <ModalBody>
                {isLoading ? null : <Bar options={options} data={data2} />}
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  بستن
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
